import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import SessionTimeoutModal from '../components/SessionTimeoutModal';
import SESSION_CONFIG from '../config/auth';

const AuthContext = createContext();

// Get timeout settings from config
const {
  INACTIVITY_TIMEOUT,
  WARNING_BEFORE_TIMEOUT,
  ACTIVITY_EVENTS
} = SESSION_CONFIG;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tracks the async role fetch separately from the session check so route
  // guards can wait for roles instead of evaluating against an empty array.
  const [rolesLoading, setRolesLoading] = useState(true);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [isExtendingSession, setIsExtendingSession] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(WARNING_BEFORE_TIMEOUT);

  const navigate = useNavigate();
  const userActivityTimeout = useRef(null);
  const sessionWarningTimeout = useRef(null);
  const lastActivity = useRef(Date.now());
  // Latest showTimeoutWarning, read inside timers without making the timer
  // callback depend on it (which would otherwise churn effects).
  const showTimeoutWarningRef = useRef(false);
  const signOutRef = useRef(null);

  useEffect(() => {
    showTimeoutWarningRef.current = showTimeoutWarning;
  }, [showTimeoutWarning]);

  // Load the authenticated user's roles from public.user_roles. RLS allows a
  // user to read their own role rows. Roles drive cosmetic nav/route gating on
  // the client; the server is the authoritative enforcer. Stable identity (no
  // deps) so it never triggers effect re-runs.
  const loadRoles = useCallback(async (userId) => {
    if (!userId) {
      setRoles([]);
      setRolesLoading(false);
      return;
    }
    setRolesLoading(true);
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      if (error) throw error;
      setRoles((data || []).map((r) => r.role));
    } catch (error) {
      console.error('Error loading user roles:', error);
      setRoles([]);
    } finally {
      setRolesLoading(false);
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (userActivityTimeout.current) clearTimeout(userActivityTimeout.current);
    if (sessionWarningTimeout.current) clearTimeout(sessionWarningTimeout.current);
  }, []);

  // Reset inactivity timers. Depends only on `user` (reads the warning flag via
  // ref), so its identity is stable across renders except on login/logout —
  // keeping the effects that use it from re-running in a loop.
  const resetActivityTimers = useCallback(() => {
    if (!user) return;

    lastActivity.current = Date.now();
    clearTimers();
    setShowTimeoutWarning(false);

    sessionWarningTimeout.current = setTimeout(() => {
      setShowTimeoutWarning(true);
      setTimeRemaining(WARNING_BEFORE_TIMEOUT);
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT);

    userActivityTimeout.current = setTimeout(() => {
      if (showTimeoutWarningRef.current) return; // let the warning modal handle it
      if (signOutRef.current) signOutRef.current(true);
    }, INACTIVITY_TIMEOUT);
  }, [user, clearTimers]);

  // Extend session
  const extendSession = useCallback(async () => {
    if (!user) return;

    setIsExtendingSession(true);
    try {
      // Refresh token if possible
      const { error } = await supabase.auth.refreshSession();
      if (error) throw error;

      // Reset activity timers
      resetActivityTimers();
    } catch (error) {
      console.error('Error extending session:', error);
      // If refresh fails, log out
      if (signOutRef.current) signOutRef.current();
    } finally {
      setIsExtendingSession(false);
      setShowTimeoutWarning(false);
    }
  }, [user, resetActivityTimers]);

  // Initialize authentication — runs once. Loads the session, resolves roles,
  // and subscribes to auth changes. Intentionally has no reactive deps so it
  // never re-subscribes / re-fetches roles in a loop.
  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const sessionUser = data?.session?.user || null;
        if (!mounted) return;
        setUser(sessionUser);
        // Resolve roles before clearing `loading` so guards don't see an
        // authenticated user with an empty (not-yet-loaded) role set.
        await loadRoles(sessionUser ? sessionUser.id : null);
      } catch (error) {
        console.error('Error checking session:', error);
        if (mounted) {
          setUser(null);
          setRoles([]);
          setRolesLoading(false);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    checkSession();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      const sessionUser = session?.user || null;
      setUser(sessionUser);
      setLoading(false);
      if (sessionUser) {
        setRolesLoading(true);
        // Defer the DB call out of the auth callback (supabase-js guidance) and
        // avoid a tick where rolesLoading is stale.
        setTimeout(() => loadRoles(sessionUser.id), 0);
      } else {
        setRoles([]);
        setRolesLoading(false);
      }
    });

    return () => {
      mounted = false;
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start / stop inactivity timers and activity listeners based on auth state.
  useEffect(() => {
    if (!user) {
      clearTimers();
      return undefined;
    }

    resetActivityTimers();
    const handleUserActivity = () => resetActivityTimers();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, handleUserActivity));

    return () => {
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, handleUserActivity));
      clearTimers();
    };
  }, [user, resetActivityTimers, clearTimers]);

  // Handle session warning countdown
  useEffect(() => {
    let countdownInterval;

    if (showTimeoutWarning) {
      countdownInterval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1000) {
            clearInterval(countdownInterval);
            if (signOutRef.current) signOutRef.current(true); // Session expired
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    }

    return () => {
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [showTimeoutWarning]);

  const signOut = useCallback(async (sessionExpired = false) => {
    try {
      clearTimers();
      setShowTimeoutWarning(false);
      await supabase.auth.signOut();
      setUser(null);
      setRoles([]);
      setRolesLoading(false);

      // Navigate to login with expired parameter if session expired
      navigate(sessionExpired ? '/login?expired=true' : '/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  }, [navigate, clearTimers]);

  // Keep a ref to the latest signOut so timers can call it without depending on it.
  useEffect(() => {
    signOutRef.current = signOut;
  }, [signOut]);

  // Role helpers for cosmetic gating. super_admin implicitly satisfies any
  // role check, matching the server-side requireRole convention.
  const hasRole = useCallback(
    (...allowed) => roles.includes('super_admin') || allowed.some((r) => roles.includes(r)),
    [roles]
  );

  const value = {
    user,
    roles,
    hasRole,
    loading,
    rolesLoading,
    signOut,
    isAuthenticated: !!user,
    extendSession
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionTimeoutModal
        open={showTimeoutWarning}
        onExtend={extendSession}
        onLogout={signOut}
        timeRemaining={timeRemaining}
        isExtending={isExtendingSession}
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
